/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./TourPanel.css";

/**
 * The runs the copy deck sets in bold, marked in the locale string as **…**.
 *
 * The deck bolds the "Example:" sentence on six of its steps and the closing
 * step's ask to keep the page fresh, and it does it mid-paragraph --
 * so the emphasis has to travel with the wording rather than sit in a field
 * beside it. Splitting on a double-asterisk pair is the whole notation: every
 * odd piece is a run the deck bolded.
 *
 * Not a markdown renderer, and not dangerouslySetInnerHTML. These strings are
 * translated in three files by people editing copy, and the moment one of them
 * can inject markup every future wording change is a security question. Text
 * nodes and <strong> cannot be anything else.
 */
export function marked(text) {
  return String(text || "")
    .split("**")
    .map((run, i) =>
      i % 2 === 1 ? <strong key={i}>{run}</strong> : run
    );
}

/**
 * The guided tour's card (CP-308).
 *
 * Drawn in two places, and it has to be the same card in both: the tour
 * itself, and the always-on (i) buttons, which reopen one step's panel on
 * demand. The deck asks for those to be the tour's panel exactly — same
 * section label, same heading, same wording — differing only in the step
 * badge, which an (i) is not in a position to carry, and in what sits on the
 * button row.
 *
 * So there is one component and one stylesheet rather than two that merely
 * resemble each other. A card that is copied is a card that drifts: the
 * first change to the tour's type or padding would leave the (i) panels a
 * version behind, and nothing would fail to say so.
 *
 * It owns no positioning. The tour places it against a spotlit element or in
 * the middle of the page; an (i) hangs it off its own button. Each caller
 * wraps it in something positioned and this only draws the card.
 */
class TourPanel extends Component {
  render() {
    const panel = this.props.panel;
    return (
      <div className="Marketplace-Tour-card">
        <div className="Marketplace-Tour-head">
          {/* Only while a step is actually running. An always-on (i) is an
              examples box rather than a position in a run, and a reader who
              opens one months later to check how to word a metric is told
              nothing by "STEP 10 OF 14" -- so tipsVm passes no label and the
              badge is not drawn. The head stays, because the section label
              still belongs there. */}
          {panel.countLabel ? (
            <span className="Marketplace-Tour-count">{panel.countLabel}</span>
          ) : null}
          <span className="Marketplace-Tour-kicker">{panel.kicker}</span>
        </div>
        <h3 className="Marketplace-Tour-title">{panel.title}</h3>
        <p className="Marketplace-Tour-body">{marked(panel.body)}</p>
        <div className="Marketplace-Tour-actions">
          {/* Real buttons, not divs. These are the only way through a layer
              that blocks every press on the page behind it, so as <div
              onClick> they were unreachable without a pointer: no role, no
              tab stop, no Enter or Space. A keyboard reader met by the tour
              on first sign-in could neither advance it nor leave. */}
          {panel.buttons.map((button) => (
            <button
              type="button"
              className={"Marketplace-Tour-btn Marketplace-Tour-btn--" + button.kind}
              onClick={button.press}
              key={button.id}
            >
              {button.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
}

TourPanel.propTypes = {
  // { countLabel, kicker, title, body, buttons: [{ id, kind, label, press }] }
  // — one shape, built by tourVm for a running step and by tipsVm for an
  // always-on panel, whose only button is Close and which passes no
  // countLabel. `body` may carry **…** runs; see `marked` above.
  panel: PropTypes.object.isRequired,
};

export default TourPanel;
